import React from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import GoogleMap from '../components/GoogleMap'

export default function Financing() {
  return (
    <>
      <Helmet>
        <title>Finansiering - Recars ApS</title>
        <meta name="description" content="Få attraktive finansieringsmuligheder til din næste brugte bil hos Recars ApS. Vi tilbyder skræddersyede løsninger med og uden udbetaling." />
        <meta property="og:title" content="Finansiering - Recars ApS" />
        <meta property="og:description" content="Få attraktive finansieringsmuligheder til din næste brugte bil hos Recars ApS. Vi tilbyder skræddersyede løsninger med og uden udbetaling." />
        <link rel="canonical" href="https://recars.dk/finansiering" />
      </Helmet>

      {/* Header */}
      <section className="relative bg-[#F5F2EB] pt-32 sm:pt-40 lg:pt-44 pb-16 border-b border-stone-300">
        <div className="mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-8 xl:px-12 text-left">
          <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#9b784a]">
            Attraktive vilkår
          </span>
          <h1 className="mt-2 text-3xl font-bold tracking-tight text-neutral-900 sm:text-4xl font-display">
            Finansiering af din næste bil
          </h1>
          <p className="mt-4 max-w-2xl text-sm text-neutral-600">
            Hos Recars ApS tilbyder vi i samarbejde med Santander Consumer Bank nogle af markedets absolut bedste og mest fleksible finansieringsløsninger.
          </p>
        </div>
      </section>

      {/* Content Section */}
      <section className="bg-[#FAF9F6] py-20">
        <div className="mx-auto max-w-[1600px] px-4 sm:px-6 lg:px-8 xl:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center mb-20">
            {/* Text */}
            <div className="text-left flex flex-col gap-6">
              <h2 className="text-2xl font-bold text-neutral-900 sm:text-3xl font-display">
                Skræddersyede løsninger, der passer til din økonomi
              </h2>
              <p className="text-sm leading-relaxed text-neutral-700">
                Uanset om du ønsker at finansiere din bil med eller uden udbetaling, kan vi hjælpe dig. Vi rådgiver dig professionelt, så du får en månedlig ydelse og en løbetid, der passer perfekt til dine behov og dit budget.
              </p>
              <p className="text-sm leading-relaxed text-neutral-700">
                Gennem vores stærke samarbejde med Santander Consumer Bank kan vi tilbyde både fast og variabel rente til yderst konkurrencedygtige priser. Sagsbehandlingen sker lynhurtigt, så du ofte kan få godkendt dit lån på under 5 minutter.
              </p>
              
              <div className="mt-4 flex flex-wrap gap-4">
                <Link
                  to="/kontakt"
                  className="rounded-xl bg-neutral-950 px-6 py-3.5 text-xs font-bold uppercase tracking-wider text-white hover:bg-[#DFB587] hover:text-neutral-950 transition-all duration-300 shadow-sm"
                >
                  Få et finansieringstilbud
                </Link>
                <Link
                  to="/biler-til-salg"
                  className="rounded-xl border border-stone-200 bg-white px-6 py-3.5 text-xs font-bold uppercase tracking-wider text-neutral-900 hover:bg-stone-50 transition-all duration-300 shadow-sm"
                >
                  Se biler på lager
                </Link>
              </div>
            </div>

            {/* Visual Card */}
            <div className="relative overflow-hidden rounded-3xl border border-stone-200/90 bg-white p-8 text-left shadow-md">
              <div className="absolute top-0 right-0 h-40 w-40 bg-[radial-gradient(circle_at_top_right,rgba(223,181,135,0.18),transparent_70%)] pointer-events-none" />
              <h3 className="font-display text-lg font-bold text-neutral-900 uppercase tracking-wider mb-6">
                Dine fordele hos os
              </h3>
              <ul className="space-y-4 text-sm text-neutral-700">
                <li className="flex items-start gap-3">
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#DFB587]/20 text-[#9b784a] mt-0.5">
                    <i className="fa-solid fa-check text-xs" />
                  </div>
                  <span>Hurtigt svar på låneansøgning (under 5 minutter)</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#DFB587]/20 text-[#9b784a] mt-0.5">
                    <i className="fa-solid fa-check text-xs" />
                  </div>
                  <span>Mulighed for finansiering helt uden udbetaling</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#DFB587]/20 text-[#9b784a] mt-0.5">
                    <i className="fa-solid fa-check text-xs" />
                  </div>
                  <span>Valgfrihed mellem fast og variabel rente</span>
                </li>
                <li className="flex items-start gap-3">
                  <div className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#DFB587]/20 text-[#9b784a] mt-0.5">
                    <i className="fa-solid fa-check text-xs" />
                  </div>
                  <span>Bevar dine lånemuligheder i banken til andre formål</span>
                </li>
              </ul>
            </div>
          </div>

          {/* Financing Types */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {/* Type 1 */}
            <div className="rounded-2xl border border-stone-200/90 bg-white p-8 text-left transition-all duration-300 hover:shadow-md shadow-sm">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#DFB587]/20 border border-[#DFB587]/40 text-[#9b784a] mb-6">
                <i className="fa-solid fa-percent text-lg" />
              </div>
              <h3 className="font-display text-lg font-semibold text-neutral-900">
                Finansiering med udbetaling
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-neutral-600">
                Dette er den mest populære løsning, som sikrer dig den absolut laveste rente. Lovkravet er minimum 20% i udbetaling (f.eks. i form af din gamle bil i bytte). Resten af beløbet afdrages over en fleksibel løbetid på op til 96 måneder.
              </p>
            </div>

            {/* Type 2 */}
            <div className="rounded-2xl border border-stone-200/90 bg-white p-8 text-left transition-all duration-300 hover:shadow-md shadow-sm">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-[#DFB587]/20 border border-[#DFB587]/40 text-[#9b784a] mb-6">
                <i className="fa-solid fa-wallet text-lg" />
              </div>
              <h3 className="font-display text-lg font-semibold text-neutral-900">
                Finansiering uden udbetaling
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-neutral-600">
                Har du ikke lyst til at binde kapital i din bil, eller ønsker du blot at bevare din opsparing? Vi tilbyder i samarbejde med Santander en yderst attraktiv finansiering helt uden udbetaling, så du kan køre afsted uden at skulle have penge op af lommen i dag.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Google Maps Section */}
      <GoogleMap />
    </>
  )
}
